import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import useContainerWidth from '@/components/useContainerWidth';
import { TrendingDown, TrendingUp } from 'lucide-react';
import HoverBar from './HoverBar';
import SplitNote from './SplitNote';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDateShort(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

// Same formatter but with the day-of-month included — used in the
// hover tooltip so the user can see exactly which day a data point
// represents (especially the last point, which is "today" rather
// than the start of the month).
function formatDateLong(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function DualLineChart({ data, isDark }) {
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [containerRect, setContainerRect] = useState(null);
  const measuredWidth = useContainerWidth(containerRef);

  const chartData = useMemo(() => {
    if (!data || data.length < 2) return null;

    const prices = data.map(d => d.price);
    const yields = data.map(d => d.yield_pct);
    const minP = Math.min(...prices);
    const maxP = Math.max(...prices);
    const minY = Math.min(...yields);
    const maxY = Math.max(...yields);
    const rangeP = maxP - minP || 1;
    const rangeY = maxY - minY || 1;

    const width = measuredWidth;
    // Phone view (< 600px): keep all labels but stagger them on two rows
    // (top / bottom / top …) instead of dropping dates. Needs a taller
    // bottom pad for the second row.
    const stagger = width < 600;
    const height = stagger ? 190 : 180;
    const padLeft = 48;
    const padRight = 48;
    const padTop = 10;
    const padBottom = stagger ? 38 : 28;
    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;

    const points = data.map((d, i) => {
      const x = padLeft + (i / (data.length - 1)) * chartW;
      const yP = padTop + chartH - ((d.price - minP) / rangeP) * chartH;
      const yY = padTop + chartH - ((d.yield_pct - minY) / rangeY) * chartH;
      return { x, yP, yY, ...d };
    });

    const pricePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.yP}`).join(' ');
    const yieldPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.yY}`).join(' ');

    // Y-axis ticks - Price (left)
    const pTicks = [];
    for (let i = 0; i <= 4; i++) {
      const val = minP + (rangeP * i) / 4;
      const y = padTop + chartH - (i / 4) * chartH;
      pTicks.push({ val: `$${val.toFixed(0)}`, y });
    }

    // Y-axis ticks - Yield (right)
    const yTicks = [];
    for (let i = 0; i <= 4; i++) {
      const val = minY + (rangeY * i) / 4;
      const y = padTop + chartH - (i / 4) * chartH;
      yTicks.push({ val: `${val.toFixed(2)}%`, y });
    }

    // X-axis labels — 7 dates; on phones they alternate between two rows
    // so they never collide.
    const xTicks = [];
    const step = Math.max(1, Math.floor(data.length / 7));
    for (let i = 0; i < data.length; i += step) {
      const x = padLeft + (i / (data.length - 1)) * chartW;
      const row = stagger ? xTicks.length % 2 : 0;
      xTicks.push({ label: formatDateShort(data[i].date), x, y: height - 5 - (row === 0 && stagger ? 11 : 0) });
    }

    return { points, pricePath, yieldPath, pTicks, yTicks, xTicks, width, height, padLeft, padRight, padTop, padBottom, chartW, chartH };
  }, [data, measuredWidth]);

  const handleMouseMove = useCallback((e) => {
    if (!chartData || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * chartData.width;

    // Find closest point
    let closest = null;
    let closestDist = Infinity;
    for (const p of chartData.points) {
      const dist = Math.abs(p.x - svgX);
      if (dist < closestDist) {
        closestDist = dist;
        closest = p;
      }
    }

    if (closest && closestDist < 30) {
      const tooltipX = ((closest.x / chartData.width) * rect.width) + rect.left;
      const tooltipY = ((closest.yP / chartData.height) * rect.height) + rect.top;
      setTooltip({
        date: formatDateLong(closest.date),
        price: closest.price,
        yield_pct: closest.yield_pct,
        svgX: closest.x,
        svgYP: closest.yP,
        screenX: tooltipX - rect.left,
        screenY: tooltipY - rect.top,
      });
      setContainerRect(rect);
    } else {
      setTooltip(null);
    }
  }, [chartData]);

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  if (!chartData) {
    return <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No chart data</div>;
  }

  return (
    <div className="rounded-lg border bg-card px-3 py-2" data-testid="yield-price-chart">
      <h3 className="text-xs font-bold mb-1">Price & Yield — 3 Year History</h3>
      <div
        ref={containerRef}
        className="relative w-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <svg
          viewBox={`0 0 ${chartData.width} ${chartData.height}`}
          className="w-full h-auto"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid lines */}
          {chartData.pTicks.map((t, i) => (
            <line key={i} x1={chartData.padLeft} y1={t.y} x2={chartData.width - chartData.padRight} y2={t.y} className="stroke-border/20" strokeDasharray="3,3" />
          ))}
          {/* Y-axis left (Price) */}
          {chartData.pTicks.map((t, i) => (
            <text key={`pl-${i}`} x={chartData.padLeft - 5} y={t.y + 3} textAnchor="end" className="fill-muted-foreground" fontSize="9" fontFamily="IBM Plex Mono, monospace">{t.val}</text>
          ))}
          {/* Y-axis right (Yield) */}
          {chartData.yTicks.map((t, i) => (
            <text key={`yr-${i}`} x={chartData.width - chartData.padRight + 5} y={t.y + 3} textAnchor="start" className="fill-muted-foreground" fontSize="9" fontFamily="IBM Plex Mono, monospace">{t.val}</text>
          ))}
          {/* X-axis labels */}
          {chartData.xTicks.map((t, i) => (
            <text key={`x-${i}`} x={t.x} y={t.y} textAnchor="middle" className="fill-muted-foreground" fontSize="9" fontFamily="IBM Plex Mono, monospace">{t.label}</text>
          ))}
          {/* Price line (blue) */}
          <path d={chartData.pricePath} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {/* Yield line (green) */}
          <path d={chartData.yieldPath} fill="none" stroke="#22c55e" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

          {/* Hover indicator */}
          {tooltip && (
            <>
              <line x1={tooltip.svgX} y1={chartData.padTop} x2={tooltip.svgX} y2={chartData.padTop + chartData.chartH} stroke={isDark ? '#555' : '#ccc'} strokeWidth="1" strokeDasharray="4,2" />
              <circle cx={tooltip.svgX} cy={tooltip.svgYP} r="4" fill="#3b82f6" stroke="white" strokeWidth="1.5" />
            </>
          )}
        </svg>

        {/* Tooltip popup */}
        {tooltip && (
          <div
            className="absolute z-10 pointer-events-none bg-card border rounded-md shadow-lg px-3 py-2 text-xs"
            style={{
              left: `${Math.min(tooltip.screenX + 12, (containerRef.current?.clientWidth || 500) - 130)}px`,
              top: `${Math.max(tooltip.screenY - 30, 0)}px`,
            }}
            data-testid="chart-tooltip"
          >
            <p className="font-semibold text-foreground">{tooltip.date}</p>
            <p><span className="text-blue-500">Price:</span> <span className="font-mono font-medium">${tooltip.price.toFixed(2)}</span></p>
            <p><span className="text-green-500">Yield:</span> <span className="font-mono font-medium">{tooltip.yield_pct.toFixed(2)}%</span></p>
          </div>
        )}
      </div>
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-1 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 inline-block rounded" /> Price</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-green-500 inline-block rounded" /> Yield</span>
      </div>
    </div>
  );
}

function AnnualDividendChart({ data, isDark }) {
  if (!data || data.length === 0) return null;
  const maxAmount = Math.max(...data.map(d => d.amount));
  const ceiling = maxAmount <= 1 ? Math.ceil(maxAmount * 4) / 4 + 0.25 :
                  maxAmount <= 5 ? Math.ceil(maxAmount) + 1 :
                  Math.ceil(maxAmount / 2) * 2 + 2;

  const yLabels = [];
  const steps = 4;
  for (let i = steps; i >= 0; i--) {
    const val = ceiling * i / steps;
    yLabels.push(`$${val % 1 === 0 ? val : val.toFixed(2)}`);
  }

  return (
    <div className="rounded-lg border bg-card px-3 py-2" data-testid="annual-dividend-chart">
      <h3 className="text-xs font-bold mb-1">Annual Dividend Over Time</h3>
      <div className="flex items-end gap-0">
        <div className="flex flex-col justify-between h-28 pr-2 shrink-0">
          {yLabels.map((label, i) => (
            <span key={i} className="text-[9px] font-mono text-muted-foreground leading-none">{label}</span>
          ))}
        </div>
        <div className="flex-1 flex items-end gap-2 sm:gap-3 h-28 border-b border-l border-border/40 pl-1">
          {data.map((d, i) => {
            const heightPct = ceiling > 0 ? (d.amount / ceiling) * 100 : 0;

            // Year-over-year growth vs the prior bar. Same clean-fallback
            // rule as DivGrowthTab: no prior year > 0 → no chip.
            const prev = i > 0 ? data[i - 1] : null;
            const base = `${d.year}: $${d.amount.toFixed(2)}`;
            let label = base;
            if (prev && prev.amount > 0) {
              const pct = ((d.amount - prev.amount) / prev.amount) * 100;
              const up = pct >= 0;
              const arrow = up ? '▲' : '▼';
              const sign = up ? '+' : '';
              const color = up ? 'text-emerald-400' : 'text-rose-400';
              label = (
                <>
                  {base}{' '}
                  <span className={color}>
                    ({arrow} {sign}{pct.toFixed(1)}% vs {prev.year})
                  </span>
                </>
              );
            }

            return (
              <div key={d.year} className="flex-1 flex flex-col items-center justify-end h-full">
                <HoverBar
                  label={label}
                  className="w-full max-w-[48px] rounded-t-sm bg-emerald-500 transition-all duration-500"
                  style={{ height: `${Math.max(heightPct, 2)}%` }}
                  isDark={isDark}
                />
                <span className="text-[9px] font-mono text-muted-foreground mt-1">{d.year}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function YieldHistoryTab({ ticker, isDark }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    axios.get(`${API}/stocks/${ticker}/yield-history`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => setError('Could not load yield history'))
      .finally(() => setLoading(false));
  }, [ticker]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading yield history...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-[200px]" data-testid="yield-history-error">
        <div className="text-center text-muted-foreground">
          <TrendingDown className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">{error || 'No yield history available'}</p>
        </div>
      </div>
    );
  }

  const badgeColor = data.badge === 'High Yield' ? 'bg-green-600 text-white' :
                     data.badge === 'Moderate Yield' ? 'bg-emerald-700 text-white' :
                     'bg-muted text-muted-foreground';

  return (
    <div className="space-y-2" data-testid="yield-history-tab">
      {/* Header metrics */}
      <div className="flex items-center gap-4 flex-wrap" data-testid="yield-metrics-row">
        <span className={`text-xs font-bold px-2.5 py-1 rounded ${badgeColor}`} data-testid="yield-badge">{data.badge}</span>
        <div className="text-center">
          <p className="text-[10px] text-muted-foreground">Current Yield</p>
          <p className="text-sm font-bold font-mono text-green-600 dark:text-green-400">{data.current_yield}%</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-muted-foreground">3yr Avg Yield</p>
          <p className="text-sm font-bold font-mono">{data.avg_yield_3yr}%</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-muted-foreground">vs Avg</p>
          <p className={`text-sm font-bold font-mono ${data.vs_avg >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {data.vs_avg >= 0 ? '+' : ''}{data.vs_avg}%
          </p>
        </div>
      </div>

      {/* Oct 2026 — split chip so a flat-looking price/yield line or bar
          chart across a split reads as what it is: split-adjusted history. */}
      <SplitNote splits={data.splits} testId="yield-history-split-note" />

      {/* Dual-line chart */}
      <DualLineChart data={data.monthly_data} isDark={isDark} />

      {/* Annual Dividend Bar Chart */}
      {data.annual_data && data.annual_data.length > 0 && (
        <AnnualDividendChart data={data.annual_data} isDark={isDark} />
      )}
    </div>
  );
}
